import EmptyState from '@/components/EmptyState';
import OrderActions from '@/features/orders/components/OrderActions';
import AcceptOrderSheet from '@/features/orders/components/AcceptOrderSheet';
import CancelOrderSheet from '@/features/orders/components/CancelOrderSheet';
import { OrderRouteMap } from '@/features/orders/components/OrderRouteMap';
import OrderBadge from '@/features/orders/components/OrderBadge';
import LabelChipRow from '@/features/labels/components/LabelChipRow';
import { safeLabelColor } from '@/features/labels/utils/label-color';
import { PaymentCountdown } from '@/features/orders/components/PaymentCountdown';
import { useLabels } from '@/features/labels/hooks/useLabels';
import { usePrintOrderTicket } from '@/features/print/hooks/usePrint';
import { useSetOrderLabels } from '@/features/orders/hooks/useOrderMutations';
import { trackOrderPrinted, useTrackOrderOpened } from '@/features/orders/hooks/useOrderAnalytics';
import {
  useOrderDetail,
  useOrderPayment,
  useOrderProof,
  useOrderRefunds,
} from '@/features/orders/hooks/useOrders';
import { useOrderActions } from '@/features/orders/hooks/useOrderActions';
import { useErrorT, useT, useI18nStore } from '@/i18n';
import { apiErrorText } from '@/api/errors';
import { copyPhoneAndPromptCall, handleContactLinkClick } from '@/utils/contactLinks';
import { isPaymentRefundable, paymentMethodLabel } from '@/features/orders/utils/payment-method';
import { paymentStatusLabel } from '@/features/orders/utils/payment-status';
import { formatMoney } from '@/utils/money';
import { orderTiming, deliveryTravelMinutes } from '@/features/orders/utils/time';
import { deliveryDistanceKmPreferRoute, formatDistanceKm } from '@/features/orders/utils/distance';
import { formatDateTime24, formatTime, fromApi } from '@/utils/datetime';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { formatOrderLineChoices, type OrderLineChoiceSnapshot } from '@restaurant/types';
import {
  Block,
  BlockTitle,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  Popover,
  Preloader,
  Segmented,
  f7,
} from 'framework7-react';
import { useRef, useState, type MouseEvent } from 'react';

/** What one line is and how the guest configured it. null when there is
 *  nothing to qualify, so the row keeps a single title. */
function itemConfigLine(item: {
  categoryNameSnapshot: string | null;
  choices: OrderLineChoiceSnapshot[];
  customerNote: string | null;
}): string | null {
  const parts = item.categoryNameSnapshot ? [item.categoryNameSnapshot] : [];
  parts.push(...formatOrderLineChoices(item.choices));
  if (item.customerNote) parts.push(item.customerNote);
  return parts.length > 0 ? parts.join(' · ') : null;
}

// Per-kind glyph for a timeline row (audit-event kind → f7-icon). Anything not
// mapped falls back to a neutral clock, so a new kind never renders blank.
const TIMELINE_ICON: Record<string, string> = {
  CONFIRMED: 'checkmark_alt',
  COMPLETED: 'checkmark_circle',
  CANCELLED: 'xmark_circle',
  PREP_ADVANCED: 'flame',
  PREP_RECALLED: 'flame',
  PREP_REMINDER_SENT: 'bell',
  PAYMENT_COLLECTED: 'money_dollar_circle',
  PAYMENT_VOIDED: 'xmark_circle',
  REFUND_ISSUED: 'arrow_uturn_left',
  PROOF_UPLOADED: 'photo',
  PROOF_ACCEPTED: 'checkmark_alt',
  PROOF_REJECTED: 'xmark_circle',
};

// Intent colors shared with OrderBadge: pending money = orange, settled = green,
// a failed provider refund = red (needs a retry / manual payout).
const REFUND_PENDING_COLOR = '#ff9500';
const REFUND_SETTLED_COLOR = '#248a3d';
const REFUND_FAILED_COLOR = '#ff3b30';

// Timing helpers are shared with OrderCard so the list and this detail page
// can never disagree about ASAP vs scheduled / overdue.

/**
 * Body-only order detail. Lives in the workspace's docked inspector pane on
 * wide layouts and inside the pushed `OrderDetailPage` on phones - so it
 * renders no Navbar/Page of its own (mirrors `ReservationInspector`).
 *
 * The contextual lifecycle popover (accept / complete / cancel) is opened from
 * the status badge - the same control as the list card. Money-moving actions
 * (refund) use a bounded input, never a free-text prompt.
 */
export default function OrderInspector({
  orderId,
  surface,
  onProofActioned,
}: {
  orderId: string | null;
  // Where this instance renders. Above 1100px the inspector docks beside the
  // list and no navigation happens, so a pageview cannot tell the two apart.
  surface: 'docked' | 'page';
  // Standalone detail page (mobile / push deep-link) passes a back-to-list
  // callback fired after a proof accept/reject; the docked inspector omits it.
  onProofActioned?: () => void;
}) {
  // `tr` (not `t`): a local `t` below already holds the order-timing result.
  const tr = useT();
  const errorT = useErrorT();
  const printTicket = usePrintOrderTicket();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const popoverRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const labelPopoverRef = useRef<any>(null);
  const { data: order, isLoading, error } = useOrderDetail(orderId ?? '');
  useTrackOrderOpened(order, surface);
  const { data: payment } = useOrderPayment(orderId ?? '');
  // Only the manual bank-transfer rail has a customer-uploaded proof. Fetch it for
  // manual payments so the owner can eyeball the transfer screenshot before (and
  // after) deciding - null for every other method / when nothing was uploaded.
  const { data: proof } = useOrderProof(orderId ?? '', payment?.methodKind === 'MANUAL');
  const { data: refunds } = useOrderRefunds(orderId ?? '');
  const restaurant = useRestaurantStore((s) => s.restaurantConfig);
  const locale = useI18nStore((s) => s.locale);

  // Order write-surface actions (lifecycle / proof / settle) live in the hook,
  // wired verbatim. It also exposes the mutation objects the render still reads
  // (`*.isPending`) and the `createRefund` the refund form submits through.
  const {
    acceptProof,
    rejectProof,
    createRefund,
    settleRefund,
    acceptTarget,
    closeAcceptSheet,
    cancelTarget,
    closeCancelSheet,
    handleAccept,
    handleComplete,
    handleCancel,
    handleAcceptProof,
    handleRejectProof,
    handleSettle,
  } = useOrderActions(order, { onProofActioned });

  // ── Triage labels ──────────────────────────────────────────────────────────
  // The chip row shows the restaurant's whole label set, so it needs both the
  // definitions and this order's assignments.
  const { data: labelOptions = [] } = useLabels();
  const setLabels = useSetOrderLabels(orderId ?? '');
  const assignedLabels = order?.labels ?? [];
  const selectedLabelIds = assignedLabels.map((l) => l.id);

  const toggleLabel = (labelId: string) => {
    const next = selectedLabelIds.includes(labelId)
      ? assignedLabels.filter((l) => l.id !== labelId)
      : [...assignedLabels, ...labelOptions.filter((l) => l.id === labelId)];
    setLabels.mutate(next, {
      onError: () =>
        f7.toast.show({
          text: tr.labels.couldNotAssign,
          closeTimeout: 2000,
          position: 'bottom',
        }),
    });
  };

  const openLabelSettings = (e: MouseEvent) => {
    e.preventDefault();
    f7.views.main.router.navigate('/settings/labels');
  };

  // Money actually returned (SETTLED only) vs recorded-but-pending. payment.refundedCents
  // counts BOTH, so the "Refunded" line must split them or it overstates what the
  // customer has received.
  const pendingRefundCents = (refunds ?? [])
    .filter((r) => r.settlementStatus === 'PENDING')
    .reduce((sum, r) => sum + r.amountCents, 0);
  const settledRefundCents = (refunds ?? [])
    .filter((r) => r.settlementStatus === 'SETTLED')
    .reduce((sum, r) => sum + r.amountCents, 0);

  const remainingRefundable = payment ? payment.amountCents - payment.refundedCents : 0;

  // Compact payment pill for the summary row directly under Status — so the money
  // state (paid / refunded / refund pending) reads without scrolling to the
  // payment section. A PENDING refund reads "Refund pending" (amber) rather than
  // the payment's optimistic REFUNDED, so it never overstates what the customer got.
  const paySummary = !payment
    ? null
    : pendingRefundCents > 0
      ? { text: tr.orders.refundExtra.pendingShort, bg: '#fef3c7', color: '#b45309' }
      : (() => {
          const text = paymentStatusLabel(
            payment.methodKind,
            payment.status,
            tr.orders.paymentStatusLabels,
          );
          if (payment.status === 'PAID') return { text, bg: '#dcfce7', color: '#15803d' };
          if (payment.status === 'PARTIALLY_REFUNDED')
            return { text, bg: '#fef3c7', color: '#b45309' };
          return { text, bg: '#f1f5f9', color: '#475569' };
        })();

  // Delivery map view — defaults to the driving route (see OrderRouteMap).
  const [mapMode, setMapMode] = useState<'route' | 'destination'>('route');

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  // Copy the delivery address to the clipboard so the operator can paste it into
  // a courier app. Falls back to a dialog where the Clipboard API is unavailable
  // (insecure context / denied) so the address is never lost behind a silent fail.
  const copyAddress = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(tr.orders.addressCopied);
    } catch {
      f7.dialog.alert(text, tr.orders.section.deliveryAddress);
    }
  };

  // The copy / open-in-maps actions moved off the always-visible rows into an
  // action sheet the operator opens by tapping the address (keeps the delivery
  // card compact now that the map sits above it).
  const openAddressActions = (fullAddress: string, mapsHref: string) => {
    f7.actions
      .create({
        buttons: [
          [
            { text: fullAddress, label: true },
            { text: tr.orders.copy, onClick: () => void copyAddress(fullAddress) },
            { text: tr.orders.openInMaps, onClick: () => window.open(mapsHref, '_blank') },
          ],
          [{ text: tr.cancel, color: 'red' }],
        ],
      })
      .open();
  };

  const openPopover = (e: React.MouseEvent) => {
    const target = e.currentTarget as HTMLElement;
    requestAnimationFrame(() => {
      if (popoverRef.current?.el) f7.popover.open(popoverRef.current.el, target);
    });
  };

  // Refund a cancelled order in one tap: the whole outstanding amount, reason
  // fixed to the cancellation. No amount input — a cancelled order refunds in
  // full, and a confirm (like accept-order) is the one guard needed.
  const handleRefundNow = () => {
    if (!order || createRefund.isPending || remainingRefundable <= 0) return;
    f7.dialog.confirm(
      tr.orders.refund.confirmNow(formatMoney(remainingRefundable, order.currency)),
      tr.orders.refund.now,
      () => {
        createRefund.mutate(
          { orderId: order.id, amountCents: remainingRefundable, reason: 'CANCELLED_BY_OWNER' },
          {
            // A rejected provider refund is NOT an HTTP error: the use-case records
            // the attempt durably and returns 2xx with settlementStatus FAILED. Only
            // SETTLED means the money actually moved — PENDING (Paycat async, cash,
            // bank transfer) still owes a payout, so neither may read as "issued".
            onSuccess: (entry) => {
              if (entry.settlementStatus === 'FAILED') {
                toast(
                  entry.failureReason
                    ? tr.orders.refund.failedReason(entry.failureReason)
                    : tr.orders.refund.failed,
                );
                return;
              }
              toast(
                entry.settlementStatus === 'SETTLED'
                  ? tr.orders.refund.issued
                  : tr.orders.refund.recordedPending,
              );
            },
            onError: (e) => toast(e instanceof Error ? e.message : tr.orders.refund.failed),
          },
        );
      },
    );
  };

  if (!orderId) {
    return (
      <EmptyState
        icon="sidebar_right"
        title={tr.orders.emptyState.noSelection.title}
        description={tr.orders.emptyState.noSelection.description}
      />
    );
  }
  if (isLoading) {
    return (
      <Block style={{ textAlign: 'center', paddingTop: 48 }}>
        <Preloader size={32} />
      </Block>
    );
  }
  if (error || !order) {
    return (
      <EmptyState icon="exclamationmark_triangle" title={tr.orders.emptyState.notFound.title} />
    );
  }

  // Refunds are now driven by cancellation: an owner can only refund once the
  // order is CANCELLED (the backend rejects createRefund on a live order). So the
  // one-tap "Refund now" shows only for a cancelled, refundable payment that
  // still owes money back.
  const canRefundNow =
    order.status === 'CANCELLED' && isPaymentRefundable(payment) && remainingRefundable > 0;

  // Timing leads the hero (it's what staff act on). The branch - Scheduled vs
  // Ready-by vs ASAP vs done - is decided once, in the shared orderTiming(),
  // which the list card also uses, so the two screens can never disagree.
  const t = orderTiming(order, tr.orders.timing);
  const timeOverdue = t.tone === 'overdue';
  const timeColor =
    t.tone === 'overdue' ? '#ff3b30' : t.tone === 'muted' ? '#8e8e93' : 'var(--f7-theme-color)';

  return (
    <>
      {/* Hero - timing leads (what staff act on); customer + type next; the
          total is demoted to a small secondary line. */}
      <Card className="rsv-hero">
        <CardContent>
          <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 88,
                borderRight: '1px solid var(--f7-list-item-border-color, #e5e5ea)',
                paddingRight: 16,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  color: '#8e8e93',
                }}
              >
                {t.label}
              </span>
              <span
                style={{
                  fontSize: t.headlineIsWord ? 22 : 30,
                  fontWeight: 700,
                  lineHeight: 1.05,
                  marginTop: 2,
                  color: timeColor,
                }}
              >
                {t.headline}
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: timeOverdue ? 700 : 400,
                  marginTop: 4,
                  textAlign: 'center',
                  color: timeOverdue ? '#ff3b30' : '#8e8e93',
                }}
              >
                {t.detail}
              </span>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 19, fontWeight: 600, lineHeight: 1.2 }}>
                {order.customerName}
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: '#8e8e93',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <i className="f7-icons" style={{ fontSize: 15 }}>
                  {order.type === 'PICKUP' ? 'bag' : 'car_fill'}
                </i>
                {order.type === 'PICKUP' ? tr.orders.type.PICKUP : tr.orders.type.DELIVERY}
              </div>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 12, color: '#8e8e93' }}>
                #{order.confirmationCode}
                <span style={{ margin: '0 6px' }}>·</span>
                {formatMoney(order.totalCents, order.currency)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reach-the-customer pills. */}
      <div className="rsv-quick-actions">
        {order.customerPhone && (
          <a
            className="rsv-quick-action external"
            href={`tel:${order.customerPhone}`}
            aria-label={tr.orders.quickAction.callCustomer}
            onClick={(e) => handleContactLinkClick(e, order.customerPhone!, tr.orders.phoneCopied)}
          >
            <i className="f7-icons">phone_fill</i>
            <span>{tr.orders.quickAction.call}</span>
          </a>
        )}
        {order.customerPhone && (
          <a
            className="rsv-quick-action external"
            href={`sms:${order.customerPhone}`}
            aria-label={tr.orders.quickAction.messageCustomer}
            onClick={(e) => handleContactLinkClick(e, order.customerPhone!, tr.orders.phoneCopied)}
          >
            <i className="f7-icons">chat_bubble_fill</i>
            <span>{tr.orders.quickAction.message}</span>
          </a>
        )}
        {/* Print the kitchen ticket. Sits in the top pill row because the owner
            who wants it is looking at the order, not at Settings → Print jobs
            (GitHub #255). The toast says QUEUED, not printed: the printer polls
            on its own schedule, so paper appears after this resolves. */}
        <button
          type="button"
          className="rsv-quick-action"
          disabled={printTicket.isPending}
          aria-label={tr.orders.quickAction.printTicketAria}
          onClick={() =>
            printTicket.mutate(order.id, {
              onSuccess: (jobs) => {
                trackOrderPrinted(order, { tickets: jobs.length });
                toast(
                  jobs.length > 1
                    ? tr.orders.quickAction.printQueuedMany(jobs.length)
                    : tr.orders.quickAction.printQueued,
                );
              },
              // apiErrorText (code lookup), not e.message or resolveErrorText:
              // for a typed AppError the server's `message` IS the code, so both
              // of those would toast "print.noPrinterConfigured" verbatim.
              onError: (e) => {
                trackOrderPrinted(order, { error: e });
                toast(apiErrorText(e, errorT, tr.orders.quickAction.printFailed));
              },
            })
          }
        >
          <i className="f7-icons">printer_fill</i>
          <span>{tr.orders.quickAction.printTicket}</span>
        </button>
      </div>

      {/* Status + Payment summary. `dividersIos` draws the F7 inter-row hairline
          (a plain strong-inset list omits it) so the two rows read as a grouped
          pair, not one merged block. */}
      <List strongIos insetIos dividersIos>
        {order.customerPhone && (
          <ListItem
            title={`${tr.orders.tel} ${order.customerPhone}`}
            link
            onClick={() =>
              copyPhoneAndPromptCall(
                order.customerPhone!,
                tr.orders.phoneCopied,
                tr.orders.quickAction.call,
                tr.no,
              )
            }
          >
            <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
              phone
            </i>
          </ListItem>
        )}
        <ListItem title={tr.orders.fields.status}>
          <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
            slider_horizontal_3
          </i>
          <div slot="after">
            <OrderBadge
              status={order.status}
              onClick={openPopover}
              testId="order-actions-trigger"
            />
          </div>
        </ListItem>
        {payment && paySummary && (
          <ListItem>
            <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
              creditcard
            </i>
            {/* Two-line title: label + method on the 2nd line (F7 dropped the
                `subtitle` prop next to a slotted `after`, so structure it here). */}
            <div slot="title">
              <div>{tr.orders.section.payment}</div>
              <div
                style={{
                  fontSize: 13,
                  marginTop: 2,
                  color: 'var(--rt-secondary, rgba(60, 60, 67, 0.6))',
                }}
              >
                {paymentMethodLabel(payment.methodKind, tr.orders.methodKind, payment.methodId)}
              </div>
            </div>
            <span
              slot="after"
              data-testid="payment-summary"
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: 8,
                whiteSpace: 'nowrap',
                background: paySummary.bg,
                color: paySummary.color,
              }}
            >
              {paySummary.text}
            </span>
          </ListItem>
        )}
      </List>

      {/* Customer note - straight under the status, above the items it applies
          to ("no onions", "ring the bell"), because the kitchen has to read it
          before it starts cooking. At the bottom of the inspector, past
          payment / refunds / timeline, it sat below the fold and got missed.
          Tinted yellow (same `rsv-note-highlight` as the reservation
          inspector's note) so it reads as an instruction, not one more grey
          row. */}
      {order.customerNote && (
        <>
          <BlockTitle>{tr.orders.section.customerNote}</BlockTitle>
          <List strongIos insetIos className="rsv-note-highlight">
            <ListItem>
              <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                bubble_left
              </i>
              <div slot="title" className="rsv-multiline">
                {order.customerNote}
              </div>
            </ListItem>
          </List>
        </>
      )}
      {/* Triage labels - the operator's own signal, so it reads before the
          item detail. Every defined label shows; tapping toggles it instantly.
          A Block, not a List: chips need a free-flow wrapping container. */}
      <BlockTitle>{tr.orders.section.labels}</BlockTitle>
      <Block>
        {labelOptions.length === 0 ? (
          <div style={{ fontSize: 14, color: 'var(--rt-secondary, rgba(60, 60, 67, 0.6))' }}>
            {tr.labels.none}{' '}
            <a href="/settings/labels" onClick={openLabelSettings}>
              {tr.labels.setUp}
            </a>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
              ...(setLabels.isPending ? { opacity: 0.6 } : undefined),
            }}
          >
            <LabelChipRow labels={assignedLabels} max={10} />
            <button
              type="button"
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                border: '1.5px dashed var(--f7-list-item-border-color, #c7c7cc)',
                background: 'transparent',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
              }}
              onClick={(e) => {
                if (labelPopoverRef.current?.el)
                  f7.popover.open(labelPopoverRef.current.el, e.currentTarget as HTMLElement);
              }}
            >
              <i className="f7-icons" style={{ fontSize: 16, color: 'var(--f7-theme-color)' }}>
                plus
              </i>
            </button>
          </div>
        )}
      </Block>
      <Popover ref={labelPopoverRef} className="order-label-popover">
        <List>
          {labelOptions.map((label) => (
            <ListItem
              key={label.id}
              checkbox
              checked={selectedLabelIds.includes(label.id)}
              title={label.name}
              onChange={() => toggleLabel(label.id)}
            >
              <span
                slot="after"
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: safeLabelColor(label.color),
                  display: 'inline-block',
                }}
              />
            </ListItem>
          ))}
        </List>
      </Popover>

      {/* Kitchen prep - read-only mirror of the Kitchen board (which owns the
          writes). Only meaningful once the order is in the kitchen. */}
      {order.status === 'CONFIRMED' && (
        <List strongIos insetIos>
          <ListItem title={tr.orders.fields.kitchen} after={tr.orders.kitchen[order.prepStatus]}>
            <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
              flame
            </i>
          </ListItem>
        </List>
      )}

      {/* Items */}
      <BlockTitle>{tr.orders.section.items}</BlockTitle>
      <List strongIos insetIos dividersIos>
        {order.items.map((item, i) => (
          <ListItem
            key={i}
            title={`${item.quantity}× ${item.nameSnapshot}`}
            after={formatMoney(item.lineTotalCents, order.currency)}
            // The picked options MUST be visible - without them the kitchen has
            // no idea what was ordered. `footer`, not a `subtitle` slot: F7 only
            // renders the subtitle inside a MEDIA list, so on this plain list it
            // was silently dropped (same shape as the table-ordering inspector).
            footer={itemConfigLine(item) ?? undefined}
          />
        ))}
      </List>

      {/* Totals */}
      <List strongIos insetIos dividersIos>
        <ListItem
          title={tr.orders.fields.subtotal}
          after={formatMoney(order.subtotalCents, order.currency)}
        />
        {order.deliveryFeeCents > 0 && (
          <ListItem
            title={tr.orders.fields.deliveryFee}
            after={formatMoney(order.deliveryFeeCents, order.currency)}
            footer={
              order.deliveryZone
                ? tr.orders.zoneLabel(
                    order.deliveryZone.label,
                    tr.orders.zoneMethod[order.deliveryZone.mode] ?? order.deliveryZone.mode,
                  )
                : undefined
            }
          />
        )}
        {order.discountCents > 0 && (
          <ListItem
            title={tr.orders.fields.discount}
            after={`−${formatMoney(order.discountCents, order.currency)}`}
          />
        )}
        {order.promoCode && <ListItem title={tr.orders.fields.promoCode} after={order.promoCode} />}
        <ListItem title={tr.orders.fields.total}>
          <b slot="after">{formatMoney(order.totalCents, order.currency)}</b>
        </ListItem>
      </List>

      {/* Delivery address */}
      {order.type === 'DELIVERY' &&
        order.deliveryAddress &&
        (() => {
          const addr = order.deliveryAddress;
          // Trim first — blank optional fields would otherwise join as ",,".
          const fullAddress = [addr.line1, addr.line2, addr.city, addr.postalCode]
            .map((p) => (p ?? '').trim())
            .filter(Boolean)
            .join(', ');
          // Prefer the snapshotted driving distance (road km); fall back to the
          // straight-line estimate, which is null when the restaurant isn't pinned.
          const km = deliveryDistanceKmPreferRoute(order, restaurant);
          // Distance, and — once the leg is known — the drive time beside it, so
          // the owner reads "how far" and "how long" together. The drive time is
          // only actionable while the order is live, so drop it once terminal.
          const orderActive = order.status === 'PENDING' || order.status === 'CONFIRMED';
          const travelMin = orderActive ? deliveryTravelMinutes(order) : null;
          const distanceBase =
            km == null ? null : tr.orders.deliveryDistance(formatDistanceKm(km, locale));
          const distanceText =
            distanceBase && travelMin != null
              ? `${distanceBase} · ${tr.orders.acceptSheet.minutes(travelMin)}`
              : distanceBase;
          // Search the address the GUEST typed, never our geocoded pin: the pin is
          // exactly what the owner opens Maps to double-check, so handing Google
          // our own coordinates would just confirm our answer back to us.
          const mapsQuery = [fullAddress, addr.country].filter(Boolean).join(', ');
          const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`;
          const origin =
            restaurant?.latitude != null && restaurant.longitude != null
              ? { latitude: restaurant.latitude, longitude: restaurant.longitude }
              : null;
          // No stored route (older orders) → lock to the destination pin.
          const effectiveMode = addr.route ? mapMode : 'destination';
          return (
            <>
              <BlockTitle>{tr.orders.section.deliveryAddress}</BlockTitle>
              <Block>
                <OrderRouteMap
                  origin={origin}
                  destination={{ latitude: addr.latitude, longitude: addr.longitude }}
                  route={addr.route}
                  mode={effectiveMode}
                />
                {addr.route && (
                  <Segmented strong style={{ marginTop: 12 }}>
                    <Button active={mapMode === 'route'} onClick={() => setMapMode('route')}>
                      {tr.orders.mapRoute}
                    </Button>
                    <Button
                      active={mapMode === 'destination'}
                      onClick={() => setMapMode('destination')}
                    >
                      {tr.orders.mapDestination}
                    </Button>
                  </Segmented>
                )}
              </Block>
              {/* Single tappable address row — its chevron signals the tap opens
                  the copy / open-in-maps action sheet. */}
              <List strongIos insetIos dividersIos>
                <ListItem
                  link
                  // F7 only renders `text` in the media layout - without this the
                  // distance line is silently dropped.
                  mediaItem
                  title={fullAddress}
                  text={distanceText ?? undefined}
                  className="item-title-multiline"
                  onClick={() => openAddressActions(fullAddress, mapsHref)}
                >
                  <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                    location
                  </i>
                </ListItem>
              </List>
            </>
          );
        })()}

      {/* Pickup location - where the customer collects the order. Delivery orders
          don't need this (the customer's delivery address is shown above). */}
      {order.type === 'PICKUP' &&
        restaurant &&
        (() => {
          const storeAddress = [restaurant.address, restaurant.city, restaurant.postalCode]
            .filter(Boolean)
            .join(', ');
          if (!storeAddress) return null;
          return (
            <>
              <BlockTitle>{tr.orders.pickupLocation}</BlockTitle>
              <List strongIos insetIos>
                <ListItem
                  mediaItem
                  title={restaurant.name}
                  text={storeAddress}
                  className="item-title-multiline"
                >
                  <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                    house
                  </i>
                  {/* HIG tinted button - matches the reservation-setup "Copy"
                      action so the Copy IS the control (not the whole row). */}
                  <button
                    slot="after"
                    type="button"
                    className="rsv-copy-btn"
                    onClick={() => copyAddress(storeAddress)}
                  >
                    <i className="f7-icons">doc_on_doc</i>
                    {tr.orders.copy}
                  </button>
                </ListItem>
              </List>
            </>
          );
        })()}

      {/* Payment */}
      {payment && (
        <>
          <BlockTitle>{tr.orders.section.payment}</BlockTitle>
          <List strongIos insetIos dividersIos>
            <ListItem
              title={tr.orders.fields.method}
              after={paymentMethodLabel(payment.methodKind, tr.orders.methodKind, payment.methodId)}
            />
            {!(
              (payment.methodKind === 'CASH' || payment.methodKind === 'CARD_ON_HANDOFF') &&
              payment.status === 'PENDING_COLLECTION'
            ) && (
              <ListItem
                title={tr.orders.fields.status}
                after={paymentStatusLabel(
                  payment.methodKind,
                  payment.status,
                  tr.orders.paymentStatusLabels,
                )}
              />
            )}
            {/* Manual (VietQR) transfer deadline - while the order is still PENDING
                and the customer owes action (awaiting proof, or re-uploading after a
                reject). The timer pauses once a proof is submitted, so it's hidden then. */}
            {order.status === 'PENDING' &&
              payment.methodKind === 'MANUAL' &&
              (payment.status === 'AWAITING_PROOF' || payment.status === 'REJECTED') &&
              order.paymentExpiresAt && (
                <ListItem title={tr.orders.autoCancelsIn}>
                  <span slot="after" style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <PaymentCountdown expiresAt={order.paymentExpiresAt} />
                    <span
                      style={{ color: 'var(--f7-text-color-secondary, #8e8e93)', fontSize: 13 }}
                    >
                      {formatDateTime24(order.paymentExpiresAt)}
                    </span>
                  </span>
                </ListItem>
              )}
            {payment.refundedCents > 0 && (
              <ListItem title={tr.orders.fields.refunded}>
                <span slot="after" style={{ textAlign: 'right' }}>
                  {formatMoney(settledRefundCents, order.currency)}
                  {pendingRefundCents > 0 && (
                    <span style={{ color: REFUND_PENDING_COLOR, marginLeft: 6 }}>
                      {tr.orders.refundExtra.pending(
                        formatMoney(pendingRefundCents, order.currency),
                      )}
                    </span>
                  )}
                </span>
              </ListItem>
            )}
          </List>

          {/* Customer-uploaded transfer proof - shown above the accept/reject
              actions so the owner reviews the screenshot before deciding (and can
              still see it afterwards). PDFs can't render inline, so link out. */}
          {proof && (
            <>
              <BlockTitle>{tr.orders.paymentProof}</BlockTitle>
              <Block>
                {proof.mimeType === 'application/pdf' ? (
                  <a href={proof.url} target="_blank" rel="noreferrer">
                    {tr.orders.proof.openPdf}
                  </a>
                ) : (
                  <a
                    href={proof.url}
                    target="_blank"
                    rel="noreferrer"
                    title={tr.orders.proof.openFullSize}
                  >
                    <img
                      src={proof.url}
                      alt={tr.orders.proof.alt}
                      style={{
                        width: '100%',
                        borderRadius: 12,
                        display: 'block',
                        border: '1px solid var(--f7-list-item-border-color, rgba(0,0,0,0.1))',
                      }}
                    />
                  </a>
                )}
                {proof.customerStatedAmountCents != null && (
                  <p style={{ fontSize: 13, color: '#8e8e93', margin: '10px 0 0' }}>
                    {tr.orders.proof.customerStated(
                      formatMoney(proof.customerStatedAmountCents, order.currency),
                    )}
                  </p>
                )}
                <p style={{ fontSize: 13, color: '#8e8e93', margin: '4px 0 0' }}>
                  {tr.orders.proof.uploaded(formatDateTime24(proof.createdAt))}
                </p>
              </Block>
            </>
          )}

          {payment.status === 'PROOF_SUBMITTED' && (
            <List strongIos insetIos dividersIos>
              <ListItem
                title={tr.orders.fields.acceptProof}
                link="#"
                style={{ color: '#248a3d' }}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  if (!acceptProof.isPending) handleAcceptProof();
                }}
              >
                <i slot="media" className="f7-icons" style={{ color: '#248a3d', fontSize: 22 }}>
                  checkmark_circle_fill
                </i>
              </ListItem>
              <ListItem
                title={tr.orders.fields.rejectProof}
                text={tr.orders.proof.rejectHint}
                link="#"
                style={{ color: '#ff3b30' }}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  if (!rejectProof.isPending) handleRejectProof();
                }}
              >
                <i slot="media" className="f7-icons" style={{ color: '#ff3b30', fontSize: 22 }}>
                  xmark_circle
                </i>
              </ListItem>
            </List>
          )}

          {(canRefundNow || (refunds?.length ?? 0) > 0) && (
            <>
              <BlockTitle>{tr.orders.section.refund}</BlockTitle>

              {/* History - each entry shows whether the money has actually moved.
                  PENDING (Paycat / cash / manual) offers a "Confirm settled" once
                  the owner has paid it out; SETTLED (PayPal, or confirmed) reads
                  green; FAILED (provider rejected the refund) reads red and shows
                  why, so the owner knows to retry / pay it out by hand. */}
              {refunds && refunds.length > 0 && (
                <List strongIos insetIos dividersIos>
                  {refunds.map((r) => {
                    const settled = r.settlementStatus === 'SETTLED';
                    const failed = r.settlementStatus === 'FAILED';
                    const reasonText = tr.orders.refund.reasons[r.reason] ?? r.reason;
                    return (
                      <ListItem
                        key={r.id}
                        title={formatMoney(r.amountCents, order.currency)}
                        footer={
                          failed && r.failureReason
                            ? `${reasonText} · ${r.failureReason}`
                            : reasonText
                        }
                      >
                        {settled ? (
                          <span
                            slot="after"
                            style={{
                              color: REFUND_SETTLED_COLOR,
                              fontSize: 13,
                              fontWeight: 600,
                            }}
                          >
                            {tr.orders.refundExtra.settled}
                          </span>
                        ) : failed ? (
                          <span
                            slot="after"
                            data-testid="refund-failed"
                            style={{
                              color: REFUND_FAILED_COLOR,
                              fontSize: 13,
                              fontWeight: 600,
                            }}
                          >
                            {tr.orders.refundExtra.failed}
                          </span>
                        ) : (
                          <Button
                            slot="after"
                            small
                            fill
                            color="orange"
                            disabled={settleRefund.isPending}
                            onClick={() => handleSettle(r.id)}
                          >
                            {tr.orders.refundExtra.confirmSettled}
                          </Button>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              )}

              {/* One-tap full refund of a cancelled order. No amount input — a
                  cancelled order refunds in full via its provider. */}
              {canRefundNow && (
                <Block>
                  <p style={{ fontSize: 13, color: '#8e8e93', marginTop: 0 }}>
                    {tr.orders.refund.info(formatMoney(remainingRefundable, order.currency))}
                  </p>
                  <Button
                    fill
                    color="orange"
                    data-testid="refund-now"
                    disabled={createRefund.isPending}
                    onClick={handleRefundNow}
                  >
                    {tr.orders.refund.now}
                  </Button>
                </Block>
              )}
            </>
          )}
        </>
      )}

      {order.customerPhone && (
        <List strongIos insetIos dividersIos>
          <ListItem
            title={`${tr.orders.tel} ${order.customerPhone}`}
            link
            onClick={() =>
              copyPhoneAndPromptCall(
                order.customerPhone!,
                tr.orders.phoneCopied,
                tr.orders.quickAction.call,
                tr.no,
              )
            }
          >
            <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
              phone
            </i>
          </ListItem>
        </List>
      )}

      {/* Timeline - the append-only history of what ACTUALLY happened, oldest
          first. Forward-looking targets (estimated-ready / scheduled slot) live
          in the hero, not here, so this stays a clean chronological log with one
          time format. "Placed" plus the audit trail are the single source: prep
          / confirmed come from the log in their true position, never from
          dedicated fields that duplicated them out of order. */}
      <BlockTitle>{tr.orders.section.timeline}</BlockTitle>
      <List strongIos insetIos dividersIos>
        {(() => {
          // Same-day events show the bare time ("19:11"); a cross-day event
          // (scheduled next-day handoff) keeps its date so the jump is legible.
          const placedDay = fromApi(order.createdAt);
          const fmtAt = (iso: string) =>
            fromApi(iso).hasSame(placedDay, 'day') ? formatTime(iso) : formatDateTime24(iso);
          const events = [
            {
              id: 'placed',
              icon: 'plus_circle',
              title: tr.orders.fields.placed,
              at: order.createdAt,
              sub: undefined as string | undefined,
            },
            ...(order.timeline ?? [])
              .filter((e) => e.kind !== 'CREATED')
              .map((e) => ({
                id: e.id,
                icon: TIMELINE_ICON[e.kind] ?? 'clock',
                title: tr.audit.orderKind[e.kind] ?? e.kind,
                at: e.createdAt,
                sub: e.note
                  ? `${tr.audit.actor[e.actorType]} · ${e.note}`
                  : tr.audit.actor[e.actorType],
              })),
          ].sort((a, b) => fromApi(a.at).toMillis() - fromApi(b.at).toMillis());
          return events.map((ev) => (
            <ListItem key={ev.id} title={ev.title} after={fmtAt(ev.at)} text={ev.sub}>
              <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                {ev.icon}
              </i>
            </ListItem>
          ));
        })()}
      </List>

      <OrderActions
        ref={popoverRef}
        order={order}
        onClose={() => f7.popover.close()}
        onAccept={handleAccept}
        onComplete={handleComplete}
        onCancel={handleCancel}
      />

      <AcceptOrderSheet order={acceptTarget} from="inspector" onClose={closeAcceptSheet} />
      <CancelOrderSheet order={cancelTarget} from="inspector" onClose={closeCancelSheet} />
    </>
  );
}
